import { useLicense } from "@coach/license-client";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AppShell } from "./components/app-shell.js";
import { AuthGate } from "./components/auth-gate.js";
import { BlockedScreen } from "./components/blocked-screen.js";
import { OfflineIndicator } from "./components/offline-indicator.js";
import { PwaInstallPrompt, PwaUpdateToast } from "./components/pwa-install.js";
import { SelectionGate } from "./components/selection-gate.js";
import { TeamLimitNotice } from "./components/team-limit-notice.js";
import { InAppBrowserProvider } from "./features/in-app-browser/in-app-browser.js";
import { IncomingHandler } from "./features/incoming/incoming-handler.js";
import { useLicenseBlockFlag } from "./lib/license-block-flag.js";
import { useLicenseAutoRefresh } from "./lib/license-refresh.js";
import { useSwLicenseBlockSync } from "./lib/sw-license-block.js";
import { useActiveOrgOwnerEmail, useActiveOrgRole } from "./lib/use-active-org.js";
import { isBillingHardBlocked, useBillingState } from "./lib/use-billing-state.js";
import { AccountPage } from "./routes/account.js";
import { AnalyticsPage } from "./routes/analytics.js";
import { BillingPage } from "./routes/billing.js";
import { BrowserPage } from "./routes/browser.js";
import { CalendarPage } from "./routes/calendar.js";
import { ClubPage } from "./routes/club.js";
import { HomePage } from "./routes/home.js";
import { MediaPage } from "./routes/media.js";
import { NoteEditorPage } from "./routes/note-editor.js";
import { PlayersPage } from "./routes/players.js";
import { SelectOrgPage } from "./routes/select-org.js";
import { SelectTeamPage } from "./routes/select-team.js";
import { TacticsPage } from "./routes/tactics.js";
import { TacticsEditorPage } from "./routes/tactics-editor.js";
import { TeamPage } from "./routes/team.js";
import { WhiteboardPage } from "./routes/whiteboard.js";
import { WhiteboardEditorPage } from "./routes/whiteboard-editor.js";

/**
 * Top-level routing.
 *
 * Home (`/`) renders its own backdrop + identity layout. Every other route
 * shares the thin `<AppShell>` (unified header + scroll container). There
 * is no sidebar or bottom-nav — navigation is launched from the home tile
 * grid and each page returns there via the back chevron in the header.
 */
export function App() {
  const { state, claims } = useLicense();
  useLicenseAutoRefresh();
  useSwLicenseBlockSync();
  // `useBillingState` is safe to call here even before billing exists; the
  // server returns a soft `{ subscription: null, ... }` shape. We only act
  // on the hard-block case where the subscription is `past_due` / `paused`
  // / `canceled` *and* `BILLING_ENFORCE` is on server-side (the header's
  // ExpiryGlyph / BlockedScreen surface mirrors the server's middleware
  // decision).
  const billing = useBillingState();
  const orgRole = useActiveOrgRole();
  const ownerEmail = useActiveOrgOwnerEmail();
  const blockFlag = useLicenseBlockFlag();
  // Router-driven pathname so the gates below re-evaluate on client-side
  // navigation (window.location would not re-render App, e.g. /billing → /).
  const { pathname } = useLocation();

  if (state.kind === "unknown" || state.kind === "blocked") {
    return <AuthGate state={state} />;
  }

  // Hard-block paths: subscription has lapsed and the server is enforcing.
  // The /billing route stays reachable so the coach can recover, and we
  // never gate it behind the BlockedScreen.
  // A valid manual license overrides Stripe — never blocked here.
  const billingBlocked = isBillingHardBlocked(billing.data);
  if (billingBlocked && pathname !== "/billing") {
    return (
      <BlockedScreen
        reason="subscription_blocked"
        orgRole={orgRole ?? undefined}
        ownerEmail={ownerEmail}
      />
    );
  }

  // Persisted block (see license-block-flag.ts): this device was blocked
  // before and the server has not confirmed a valid license yet in this
  // session — e.g. "online" without reaching the server. The refresh /
  // billing hooks above keep running and lift this as soon as the server
  // says valid; an explicit denial turns it into the regular blocked gate.
  // Checked after the billing gate so a reachable server's hard block shows
  // its own reason instead of the "no connection" text.
  if (blockFlag.persistedBlocked && !blockFlag.serverConfirmed && pathname !== "/billing") {
    return (
      <BlockedScreen
        detail="Die Lizenz wird beim nächsten Kontakt mit dem Server erneut geprüft. Bitte stelle eine Internetverbindung her."
        orgRole={orgRole ?? undefined}
        ownerEmail={ownerEmail}
      />
    );
  }

  return (
    <InAppBrowserProvider>
      <SelectionGate userId={claims?.sub ?? null}>
        <Routes>
          <Route path="/select/org" element={<SelectOrgPage />} />
          <Route path="/select/team" element={<SelectTeamPage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/tactics" element={<TacticsPage />} />
          <Route path="/tactics/:boardId" element={<TacticsEditorPage />} />
          <Route path="/whiteboard" element={<WhiteboardPage />} />
          <Route path="/whiteboard/note/:noteId" element={<NoteEditorPage />} />
          <Route path="/whiteboard/:boardId" element={<WhiteboardEditorPage />} />
          <Route
            path="/players"
            element={
              <AppShell title="Spieler">
                <PlayersPage />
              </AppShell>
            }
          />
          <Route
            path="/analytics"
            element={
              <AppShell title="Teamanalyse">
                <AnalyticsPage />
              </AppShell>
            }
          />
          <Route
            path="/media"
            element={
              <AppShell title="Bilder & Videos">
                <MediaPage />
              </AppShell>
            }
          />
          <Route
            path="/calendar"
            element={
              <AppShell title="Kalender">
                <CalendarPage />
              </AppShell>
            }
          />
          <Route
            path="/account"
            element={
              <AppShell title="Konto">
                <AccountPage />
              </AppShell>
            }
          />
          <Route
            path="/club"
            element={
              <AppShell title="Verein">
                <ClubPage />
              </AppShell>
            }
          />
          <Route
            path="/team"
            element={
              <AppShell title="Team">
                <TeamPage />
              </AppShell>
            }
          />
          {/* Browser ist chromeless — die Seite bringt eigene Action-Bar
            mit URL-Leiste, History-Buttons usw. mit. */}
          <Route path="/browser" element={<BrowserPage />} />
          <Route
            path="/billing"
            element={
              <AppShell title="Abrechnung">
                <BillingPage />
              </AppShell>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SelectionGate>
      <PwaInstallPrompt />
      <PwaUpdateToast />
      <OfflineIndicator />
      <TeamLimitNotice />
      <IncomingHandler />
    </InAppBrowserProvider>
  );
}
